'use client';

/**
 * Small browser-side helpers the organiser auth screens share.
 *
 * Everything here lives in sessionStorage, localStorage or a session cookie
 * and holds no answers from the application (those live on the server, so a
 * sign out on a shared computer leaves nothing behind). Every access is
 * guarded: storage can be blocked, and nothing here may break signing in.
 */

const EMAIL_KEY = 'fv_auth_email';
const EPHEMERAL_KEY = 'fv_ephemeral_session';
const ALIVE_COOKIE = 'fv_session_alive';

function session(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function local(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** Carry the typed email across the sign in / sign up switch. */
export function rememberEmail(email: string) {
  try {
    const s = session();
    if (!s) return;
    if (email.trim()) s.setItem(EMAIL_KEY, email.trim());
    else s.removeItem(EMAIL_KEY);
  } catch {
    /* storage blocked: the email simply isn't carried over */
  }
}

export function recallEmail(): string {
  try {
    return session()?.getItem(EMAIL_KEY) ?? '';
  } catch {
    return '';
  }
}

/** Signing out mid sign up clears everything this flow kept in the browser. */
export function clearAuthHandoff() {
  try {
    const s = session();
    s?.removeItem(EMAIL_KEY);
    local()?.removeItem(EPHEMERAL_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * "Keep me signed in on this device".
 *
 * Clerk's session lifetime is one setting for the whole instance, so there is
 * no per-sign-in "remember me" to ask it for. Unticked, we remember that this
 * browser asked for a short session and drop a cookie with no expiry, which
 * the browser clears when it closes. SessionLifetimeGuard then signs out on
 * the next visit if the flag is set and the cookie has gone.
 */
export function applyKeepSignedIn(keep: boolean) {
  try {
    if (keep) {
      local()?.removeItem(EPHEMERAL_KEY);
      return;
    }
    local()?.setItem(EPHEMERAL_KEY, '1');
    document.cookie = `${ALIVE_COOKIE}=1; path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
  } catch {
    /* storage blocked: the session follows Clerk's normal lifetime */
  }
}

/** True when this browser asked not to be kept signed in and has since been closed. */
export function sessionShouldEnd(): boolean {
  try {
    if (local()?.getItem(EPHEMERAL_KEY) !== '1') return false;
    return !document.cookie.split(';').some((c) => c.trim().startsWith(`${ALIVE_COOKIE}=`));
  } catch {
    return false;
  }
}

export function forgetEphemeralSession() {
  try {
    local()?.removeItem(EPHEMERAL_KEY);
  } catch {
    /* ignore */
  }
}
